import type { PropsWithChildren } from 'react';
import styled, { css } from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Skeleton as LibSkeleton } from '@elfsight/widget-ui';
import {
  createReadableClass,
  useStableBreakpoints
} from '@elfsight/widget-utils';
import { CLASS_COMPONENT } from '../../common';
import { Section } from './section';

const LOADER_BREAKPOINTS = {
  mobile: 600,
  tablet: 768
};

const DETAILS_HEIGHT = 78;
const DETAILS_WITHOUT_NAME_HEIGHT = 50;
const TOTAL_HEIGHT = 22;
const PAYMENT_TITLE_HEIGHT = 22;
const PAY_BUTTON_WITH_DESCRIPTION_HEIGHT = 72;

const TAB_SKELETON_HEIGHT = 64.8;
const FIELD_SKELETON_HEIGHT = 50;

export function CheckoutLoadingContainer({
  textColor,
  isLoading,
  detailsWithName = true,
  children
}: PropsWithChildren<{
  textColor: string;
  detailsWithName?: boolean;
  isLoading: boolean;
}>) {
  return (
    <LoaderContainer>
      <Show when={isLoading}>
        <Section $withBottomDivider $withoutTopPadding $color={textColor}>
          <Skeleton
            height={
              detailsWithName ? DETAILS_HEIGHT : DETAILS_WITHOUT_NAME_HEIGHT
            }
          />
        </Section>

        <Section $withBottomDivider $color={textColor}>
          <Skeleton height={TOTAL_HEIGHT} />
        </Section>

        <Section $color={textColor}>
          <Skeleton height={PAYMENT_TITLE_HEIGHT} />
        </Section>

        <StripeFormLoader />

        <Section>
          <Skeleton height={PAY_BUTTON_WITH_DESCRIPTION_HEIGHT} />
        </Section>
      </Show>
      <ContentContainer $isLoading={isLoading}>{children}</ContentContainer>
    </LoaderContainer>
  );
}

type TabSkeletonName = 'tab-one' | 'tab-two';
type FieldSkeletonName = 'card-num' | 'exp-date' | 'sec-code' | 'region';
type LoaderSkeletonName = TabSkeletonName | FieldSkeletonName;

export function StripeFormLoadingContainer({
  isLoading,
  children
}: PropsWithChildren<{
  isLoading: boolean;
}>) {
  return (
    <LoaderContainer>
      <Show when={isLoading}>
        <StripeFormLoader />
      </Show>
      <ContentContainer $isLoading={isLoading}>{children}</ContentContainer>
    </LoaderContainer>
  );
}

function StripeFormLoader() {
  const [measureRef, breakpoint] = useStableBreakpoints(
    LOADER_BREAKPOINTS,
    'desktop'
  );

  return (
    <StripeFormLoaderLayout data-breakpoint={breakpoint} ref={measureRef}>
      <Skeleton height={TAB_SKELETON_HEIGHT} area="tab-one" />
      <Skeleton height={TAB_SKELETON_HEIGHT} area="tab-two" />

      <Skeleton height={FIELD_SKELETON_HEIGHT} area="card-num" />
      <Skeleton height={FIELD_SKELETON_HEIGHT} area="exp-date" />
      <Skeleton height={FIELD_SKELETON_HEIGHT} area="sec-code" />
      <Skeleton height={FIELD_SKELETON_HEIGHT} area="region" />
    </StripeFormLoaderLayout>
  );
}

const StripeFormLoaderLayout = styled.div.attrs(() => ({
  className: createReadableClass({
    component: CLASS_COMPONENT,
    element: 'payment-form-loader-layout'
  })
}))`
  width: 100%;
  display: grid;
  gap: 20px;

  &[data-breakpoint='mobile'] {
    grid-template-areas:
      'tab-one tab-two'
      'card-num card-num'
      'exp-date exp-date'
      'sec-code sec-code'
      'region region';
  }

  &[data-breakpoint='tablet'] {
    grid-template-areas:
      'tab-one tab-two'
      'card-num card-num'
      'exp-date sec-code'
      'region region';
  }

  &[data-breakpoint='desktop'] {
    grid-template-areas:
      'tab-one tab-one tab-two tab-two'
      'card-num card-num exp-date sec-code'
      'region region region region';
  }
`;

function Skeleton({
  height,
  area
}: {
  height: number | string;
  area?: LoaderSkeletonName;
}) {
  return (
    <AreaSkeleton width="100%" borderRadius={8} height={height} $area={area} />
  );
}

const AreaSkeleton = styled(LibSkeleton)<{ $area?: LoaderSkeletonName }>`
  ${({ $area }) => $area && `grid-area: ${$area}`};
`;

const LoaderContainer = styled.div`
  position: relative;
  width: 100%;
  transition: height 0.5s ease-out;
`;

const ContentContainer = styled.div<{
  $isLoading: boolean;
}>`
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      position: absolute;
      opacity: 0;
    `}
`;
